import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Stack, Text, VStack } from '@chakra-ui/react';
import dayjs from 'dayjs';

import { Button } from '@m/components-ui/button';
import { snapToSelectableTime } from '@m/components-ui/date-and-time/useCalendar';
import { PageHeader } from '@m/components-ui/page-header';
import { ArrowBack } from '@m/icons';
import { LocationSuggestionType } from '@m/types/api/LocationSuggestion';
import { DateTimeFieldRow } from 'apps/customer/components/reservation/DateTimeFieldRow';
import { ReservationDateTimeDialog } from 'apps/customer/components/reservation/ReservationDateTimeDialog';
import { ReservationWindowError } from 'apps/customer/components/reservation/ReservationWindowError';

import { getReservationWindowValidation } from '../utils';

import { ReservationSearch, SearchDestination } from './ReservationSearch';

interface ReservationFormProps {
  authenticated: boolean;
  /**
   * When the window has expired (e.g. the form sat open past a prefilled
   * "now" start), bump start/end forward to the next selectable time and
   * submit instead of blocking on "Fix date & time". Off by default — a real
   * reservation should never silently change the time the customer chose.
   */
  autoCorrectExpiredWindow?: boolean;
  /**
   * When false, Starts/Ends stay visible but closed. Defaults to a committed
   * pin (or marketing lockReservationWindow). Venue / typed place searches pass
   * true so the pickers work even if discover returned 0 lots.
   */
  datesEnabled?: boolean;
  /**
   * Starts/Ends are visible but not editable — matches desktop event locking
   * (`DiscoveryDesktopView` datesLocked). Parent keeps the padded window in sync.
   */
  datesLocked?: boolean;
  destinations: SearchDestination[];
  hasCommittedSearchLocation: boolean;
  /** Seeds the Ends field; the form keeps its own copy once the user edits it. */
  initialEndDateTime?: Date;
  /** Seeds the Starts field when the form mounts (e.g. re-entering with a window already set). */
  initialStartDateTime?: Date;
  /**
   * The caller is resolving near-me coordinates. Shows the submit as busy, which also
   * keeps further taps from being dropped by the caller's in-flight lookup guard.
   */
  locating?: boolean;
  /**
   * Keep Starts/Ends always prefilled and non-clearable (end defaults to
   * start + 2h). Marketing uses this alone so dates stay editable; pair with
   * `datesLocked` for event ticket windows that must not be changed.
   */
  lockReservationWindow?: boolean;
  /** Shown when "Use my current location" cannot resolve a location. */
  nearMeErrorText?: string;
  /** Back — optional draft dates so the caller can prefill home / prior screens. */
  onBack: (draft?: { endDateTime?: Date; startDateTime?: Date }) => void;
  /** Clears the committed parent window immediately so discover drive-up rates reload. */
  onClearDateRange?: () => void;
  /** Clears the location field only — does not refetch lots. */
  onClearSearch?: () => void;
  /** Commits the selected location + optional window. Fired when "Find parking" is pressed. */
  onFindParking: (startDateTime?: Date, endDateTime?: Date) => void;
  onLoginClick: () => void;
  onRestoreSearchQuery?: (value: string) => void;
  /** Returns `false` when the search could not commit (debounce pending / no results). */
  onSearch: () => boolean | void;
  onSearchQueryChange: (value: string) => void;
  onSelectDestination: (dest: SearchDestination) => void;
  /**
   * Optional `onError` is invoked when near-me cannot resolve (denied /
   * unavailable) so the form can drop a pending auto-submit.
   */
  onUseCurrentLocation?: (onError?: () => void) => void;
  /** Location field placeholder — defaults to the `ReservationSearch` copy. */
  placeholder?: string;
  /**
   * The moment a location commits (and no window is set yet), default
   * Starts/Ends to "now" / "now + 2h" instead of leaving them empty. Off by
   * default — browsing without a window is the customer app's normal start.
   */
  prefillWindowOnCommit?: boolean;
  recentSearches?: SearchDestination[];
  searchQuery: string;
  selectedSuggestionType?: LocationSuggestionType;
  /** When false, hide Starts/Ends date pickers. */
  shouldShowDatePicker?: boolean;
  showSearchEmptyState?: boolean;
  /** Editing an existing results search: the submit reads "Update" instead of "Find parking". */
  updating?: boolean;
}

export function ReservationForm({
  authenticated,
  autoCorrectExpiredWindow = false,
  destinations,
  hasCommittedSearchLocation,
  initialEndDateTime,
  initialStartDateTime,
  locating = false,
  onBack,
  onClearDateRange,
  onFindParking,
  onLoginClick,
  onClearSearch,
  onSearch,
  onSearchQueryChange,
  onRestoreSearchQuery,
  onSelectDestination,
  onUseCurrentLocation,
  nearMeErrorText = 'Please enter an address or city.',
  placeholder,
  prefillWindowOnCommit = false,
  datesLocked = false,
  datesEnabled: datesEnabledProp,
  lockReservationWindow = false,
  recentSearches,
  searchQuery,
  selectedSuggestionType,
  showSearchEmptyState = false,
  shouldShowDatePicker = true,
  updating = false,
}: ReservationFormProps) {
  const [startDateTime, setStartDateTime] = useState<Date | undefined>(initialStartDateTime);
  const [endDateTime, setEndDateTime] = useState<Date | undefined>(initialEndDateTime);
  const [activePicker, setActivePicker] = useState<'start' | 'end' | null>(null);
  const [nearMeFailed, setNearMeFailed] = useState(false);
  /** Set when Find parking is tapped with no location — auto-continues once near-me commits. */
  const pendingNearMeSubmitRef = useRef(false);
  const datesEnabled = datesEnabledProp ?? (hasCommittedSearchLocation || lockReservationWindow);

  // Keep local fields in sync when the parent window is cleared externally
  // (e.g. results summary clear) while this form is open.
  useEffect(() => {
    if (lockReservationWindow) return;
    if (!initialStartDateTime && !initialEndDateTime) {
      setStartDateTime(undefined);
      setEndDateTime(undefined);
    }
  }, [initialStartDateTime, initialEndDateTime, lockReservationWindow]);

  // Clear dates when pickers are no longer enabled (empty / synthetic query).
  // Match DiscoveryLandingPage: typed place/venue queries keep datesEnabled even
  // without a committed pin, so do not wipe on pin-drop alone.
  const hadDatesEnabledRef = useRef(datesEnabled);
  useEffect(() => {
    if (lockReservationWindow) return;
    const hadDatesEnabled = hadDatesEnabledRef.current;
    hadDatesEnabledRef.current = datesEnabled;
    if (datesEnabled) return;
    setActivePicker(null);
    setStartDateTime(undefined);
    setEndDateTime(undefined);
    if (hadDatesEnabled) onClearDateRange?.();
  }, [datesEnabled, lockReservationWindow, onClearDateRange]);

  useEffect(() => {
    if (datesLocked) setActivePicker(null);
  }, [datesLocked]);

  useEffect(() => {
    if (lockReservationWindow) {
      // Prefer the parent event-padded window once it arrives — do not keep a
      // now+2h seed that raced ahead of DiscoveryFullscreenView's pad effect.
      if (initialStartDateTime && initialEndDateTime) {
        // Event windows stay locked to the parent. Marketing (editable) only
        // adopts parent values when local fields are still empty so edits stick.
        if (datesLocked || (!startDateTime && !endDateTime)) {
          setStartDateTime(initialStartDateTime);
          setEndDateTime(initialEndDateTime);
        }
        return;
      }
      if (startDateTime || endDateTime) return;
      const start = snapToSelectableTime(new Date());
      setStartDateTime(start);
      setEndDateTime(dayjs(start).add(2, 'hour').toDate());
      return;
    }
    if (!prefillWindowOnCommit || !hasCommittedSearchLocation) return;
    if (startDateTime || endDateTime) return;
    // The first selectable time, not literal "now" — a raw `new Date()` reads
    // as already-past the instant the next validation check runs, which would
    // lock the submit button before the visitor even sees the form.
    const start = snapToSelectableTime(new Date());
    setStartDateTime(start);
    setEndDateTime(dayjs(start).add(2, 'hour').toDate());
  }, [
    prefillWindowOnCommit,
    lockReservationWindow,
    datesLocked,
    hasCommittedSearchLocation,
    startDateTime,
    endDateTime,
    initialStartDateTime,
    initialEndDateTime,
  ]);

  // Force a re-render periodically so a window that goes stale while the form
  // sits idle (no clicks/typing) still gets caught by the wall-clock check
  // below, instead of only on the next unrelated interaction.
  const [, forceRevalidate] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceRevalidate((tick) => tick + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // Recompute every render — validity depends on wall-clock `now`, so memoizing
  // by dates alone would leave a just-expired start looking valid.
  const windowValidation =
    (!hasCommittedSearchLocation && !lockReservationWindow) || !shouldShowDatePicker
      ? { message: null as string | null, startInvalid: false, endInvalid: false }
      : getReservationWindowValidation(startDateTime, endDateTime);

  // No location yet: default to "parking near me" instead of blocking submission —
  // only when a near-me handler is actually available (geo/region resolved).
  const canDefaultToNearMe = !hasCommittedSearchLocation && !!onUseCurrentLocation;
  const canSubmit = (hasCommittedSearchLocation && !windowValidation.message) || canDefaultToNearMe;
  const readyLabel = updating ? 'Update' : 'Find parking';
  let submitLabel = 'Select an option';
  if (hasCommittedSearchLocation) {
    submitLabel = windowValidation.message ? 'Fix date & time' : readyLabel;
  } else if (canDefaultToNearMe) {
    submitLabel = readyLabel;
  }

  const clearDates = () => {
    setStartDateTime(undefined);
    setEndDateTime(undefined);
    // Clear parent window immediately so market quotes drop and discover
    // drive-up rates refetch (don't wait for Find parking).
    onClearDateRange?.();
  };

  const handleFindParking = useCallback(() => {
    if (!hasCommittedSearchLocation) {
      if (!onUseCurrentLocation) return;
      pendingNearMeSubmitRef.current = true;
      setNearMeFailed(false);
      onUseCurrentLocation(() => {
        pendingNearMeSubmitRef.current = false;
        setNearMeFailed(true);
      });
      return;
    }
    if (shouldShowDatePicker) {
      // Fresh clock check so a window that expired while the form sat open can't submit.
      if (getReservationWindowValidation(startDateTime, endDateTime).message) {
        if (!autoCorrectExpiredWindow || !startDateTime) return;
        const correctedStart = snapToSelectableTime(startDateTime);
        // Preserve the original start→end gap (e.g. a 2h window stays 2h)
        // instead of independently snapping the end to the next open slot.
        const correctedEnd = endDateTime
          ? new Date(correctedStart.getTime() + (endDateTime.getTime() - startDateTime.getTime()))
          : undefined;
        // Still invalid after correction (e.g. nothing selectable left today) — don't submit.
        if (getReservationWindowValidation(correctedStart, correctedEnd).message) return;
        setStartDateTime(correctedStart);
        setEndDateTime(correctedEnd);
        onFindParking(correctedStart, correctedEnd);
        return;
      }
      onFindParking(startDateTime, endDateTime);
      return;
    }
    // Date-picker kill-switch off — location-only discover handoff.
    onFindParking(undefined, undefined);
  }, [
    autoCorrectExpiredWindow,
    hasCommittedSearchLocation,
    onFindParking,
    onUseCurrentLocation,
    shouldShowDatePicker,
    startDateTime,
    endDateTime,
  ]);

  // Auto-continue once the near-me commit lands (see handleFindParking above).
  // Marketing lockReservationWindow keeps dates live, so submit with the window.
  // Customer home only auto-continues when dates aren't required.
  useEffect(() => {
    if (!hasCommittedSearchLocation || !pendingNearMeSubmitRef.current) return;
    pendingNearMeSubmitRef.current = false;
    setNearMeFailed(false);
    if (lockReservationWindow || !shouldShowDatePicker) handleFindParking();
  }, [hasCommittedSearchLocation, lockReservationWindow, shouldShowDatePicker, handleFindParking]);

  // After a location commit, stay on the form — do not auto-open the calendar.
  // The user opts into Starts/Ends manually when they want a time window.
  const handleSelectDestination = useCallback(
    (dest: SearchDestination) => {
      setNearMeFailed(false);
      onSelectDestination(dest);
      setActivePicker(null);
    },
    [onSelectDestination],
  );

  const handleUseCurrentLocation = useCallback(() => {
    onUseCurrentLocation?.(() => {
      setNearMeFailed(true);
    });
    setActivePicker(null);
  }, [onUseCurrentLocation]);

  const handleSearchQueryChange = useCallback(
    (value: string) => {
      setNearMeFailed(false);
      onSearchQueryChange(value);
    },
    [onSearchQueryChange],
  );

  const handleLocationSearch = useCallback((): boolean | void => {
    const committed = onSearch();
    if (committed === false) return false;
    setActivePicker(null);
    return committed;
  }, [onSearch]);

  return (
    <Box
      data-testid="ReservationForm"
      position="fixed"
      top={0}
      left={0}
      width="100vw"
      height="100dvh"
      zIndex="modal"
      display="flex"
      flexDirection="column"
      bg="fill.background.default"
    >
      <Box flexShrink={0}>
        <PageHeader
          leftIcon={<ArrowBack />}
          onLeftIconClick={() => onBack({ startDateTime, endDateTime })}
          centerContent={
            <Text variant="MainBodyDemiBold" color="text.primary">
              Find parking
            </Text>
          }
          rightAction={!authenticated ? { label: 'Log in', onClick: onLoginClick } : undefined}
        />
      </Box>

      <Box flex={1} overflowY="auto" px={4} pt={4} pb={6}>
        <Stack gap={4}>
          <VStack align="stretch" gap={1}>
            <Text variant="SmallBodyMedium" color="text.secondary">
              Where to?
            </Text>
            <ReservationSearch
              triggerVariant="field"
              allowClearLocation
              onClearSearch={onClearSearch ?? (() => onSearchQueryChange(''))}
              searchQuery={searchQuery}
              onSearchQueryChange={handleSearchQueryChange}
              onRestoreSearchQuery={onRestoreSearchQuery}
              onSearch={handleLocationSearch}
              destinations={destinations}
              onSelectDestination={handleSelectDestination}
              onUseCurrentLocation={onUseCurrentLocation ? handleUseCurrentLocation : undefined}
              placeholder={placeholder}
              recentSearches={recentSearches}
              showEmptyState={showSearchEmptyState}
              // Event windows stay in Starts/Ends. The Where to field stays the
              // single-line search box (magnifying glass), not the results summary.
              selectedSuggestionType={
                selectedSuggestionType === 'event' ? undefined : selectedSuggestionType
              }
              invalid={nearMeFailed}
              errorText={nearMeFailed ? nearMeErrorText : undefined}
            />
          </VStack>

          {shouldShowDatePicker && (
            <>
              <DateTimeFieldRow
                label="Starts"
                value={startDateTime}
                placeholder="Select your start date and time"
                disabled={!datesEnabled}
                readOnly={datesLocked}
                isActive={activePicker === 'start'}
                invalid={windowValidation.startInvalid}
                onClick={() => {
                  if (datesLocked) return;
                  if (!datesEnabled) return;
                  setActivePicker('start');
                }}
                onClear={
                  lockReservationWindow || datesLocked || !(startDateTime || endDateTime)
                    ? undefined
                    : clearDates
                }
              />

              <DateTimeFieldRow
                label="Ends"
                value={endDateTime}
                placeholder="Select your end date and time"
                disabled={!datesEnabled || !startDateTime}
                readOnly={datesLocked}
                isActive={activePicker === 'end'}
                invalid={windowValidation.endInvalid}
                onClick={() => {
                  if (datesLocked) return;
                  if (!datesEnabled || !startDateTime) return;
                  setActivePicker('end');
                }}
                onClear={
                  lockReservationWindow || datesLocked || !(startDateTime || endDateTime)
                    ? undefined
                    : clearDates
                }
              />

              {windowValidation.message && (
                <ReservationWindowError message={windowValidation.message} />
              )}
            </>
          )}
        </Stack>
      </Box>

      <Box flexShrink={0} px={4} pb={6} pt={2}>
        <Button
          variant="brand"
          w="full"
          disabled={!canSubmit}
          loading={locating}
          loadingText="Finding your location"
          onClick={handleFindParking}
        >
          {submitLabel}
        </Button>
      </Box>

      {shouldShowDatePicker && !datesLocked && (
        <>
          <ReservationDateTimeDialog
            mode="start"
            open={activePicker === 'start'}
            value={startDateTime}
            otherDate={endDateTime}
            onOpenChange={(open) => !open && setActivePicker(null)}
            onSubmit={(date) => {
              setStartDateTime(date);
              // Default the end to start + 2h whenever it's unset or no longer after start.
              setEndDateTime((prevEnd) =>
                !prevEnd || prevEnd <= date ? dayjs(date).add(2, 'hour').toDate() : prevEnd,
              );
              setActivePicker(null);
            }}
          />
          <ReservationDateTimeDialog
            mode="end"
            open={activePicker === 'end'}
            value={endDateTime}
            otherDate={startDateTime}
            onOpenChange={(open) => !open && setActivePicker(null)}
            onSubmit={(date) => {
              setEndDateTime(date);
              setActivePicker(null);
            }}
          />
        </>
      )}
    </Box>
  );
}
